import { Button } from "@components/atoms/Button"
import { IconClose } from "@components/atoms/Icon"
import { Select } from "@components/atoms/Select"
import { StaggerTransitionRoot } from "@components/motion/Transitioned"
import { CaseStudyCarousel } from "@components/site/CaseStudyCarousel"
import { defineBlock, InnerBlocks } from "eddev/blocks"
import { useSearchParams } from "eddev/routing"
import { AnimatePresence, motion, stagger } from "motion/react"
import { hash } from "object-code"
import { ReactNode, useEffect } from "react"

export const meta: BlockMeta = {
  title: "Work Listing",
  icon: "hammer",
  tags: ["#root"],
  conditions: {
    postTypes: ["page"],
    templates: ["template-work"],
    multiple: false,
  },
  category: "embed",
}

export default defineBlock("work-page/work-listing", (props) => {
  const [filterValues, filterApi] = useSearchParams({
    location: [] as string[],
    service: [] as string[],
    category: [] as string[],
    mode: "auto" as "auto" | "all" | "featured",
  })

  const filterKey = hash(filterValues)

  const hasFilters =
    filterValues.location.length > 0 || filterValues.service.length > 0 || filterValues.category.length > 0

  const showing = hasFilters ? "all" : filterValues.mode === "all" ? "all" : "featured"

  let resultDisplay: ReactNode = null

  if (showing === "all") {
    const results = props.caseStudies?.nodes.filter((node) => {
      if (
        filterValues.service.length &&
        node.serviceCategories?.nodes.some((s) => filterValues.service.includes(s.slug!)) === false
      ) {
        return false
      }
      if (
        filterValues.location.length &&
        node.locations?.nodes.some((l) => filterValues.location.includes(l.slug!)) === false
      ) {
        return false
      }
      return true
    })
    if (results?.length === 0) {
      // No results
      resultDisplay = (
        <div className="w-cols-12 mx-auto flex flex-col gap-3 pt-8 pb-10">
          <p className="type-title-m">No case studies found matching your criteria.</p>
          <p className="text-grey-dark">Try adjusting your filters or clearing them to see all case studies.</p>
          <div>
            <Button
              variant="primary"
              onClick={() => filterApi.setAll({ location: [], service: [], category: [], all: "" })}
            >
              Clear Filters
            </Button>
          </div>
        </div>
      )
    } else {
      // Filtered results
      resultDisplay = (
        <div className="w-cols-12 mx-auto flex flex-col gap-5 py-8">
          {results?.map((node) => {
            return (
              <CaseStudyCarousel
                title={node.title!}
                uri={node.uri!}
                subtitle={node.pageHeaderCaseStudy?.subtitle || undefined}
                flowgram={node.pageHeaderCaseStudy?.flowgramImage || undefined}
                images={node.pageHeaderCaseStudy?.featuredImages || []}
                theme={node.pageTheme ?? "olive"}
              />
            )
          })}
        </div>
      )
    }
  } else {
    // Show the CMS-authored content
    resultDisplay = (
      <div className="mt-6">
        <InnerBlocks allowedBlocks={["content/case-study-list", "content/case-study-grid"]} />
      </div>
    )
  }

  useEffect(() => {
    const filterBar = document.querySelector('[data-part="filter-bar"]') as HTMLElement
    if (!filterBar) return
    const bounds = filterBar.getBoundingClientRect()
    const scrollTop = document.documentElement.scrollTop!
    if (bounds.top < 0 && scrollTop > 0) {
      filterBar.scrollIntoView({ behavior: "smooth", block: "start" })
    }
  }, [filterKey])

  return (
    <div className="work-listing">
      {/* Filter bar */}
      {/* <motion.div className="w-cols-12 mx-auto" data-part="filter-bar">
        <div className="flex gap-3" data-transition="children/fade">
          <Select
            label="Service"
            options={props.serviceCategories?.nodes.map((node) => ({ value: node.slug!, label: node.name! })) ?? []}
            multiple
            value={filterApi.get("service")}
            onChange={filterApi.setter("service")}
          />

          <Select
            label="Location"
            options={props.locations?.nodes.map((node) => ({ value: node.slug!, label: node.name! })) ?? []}
            multiple
            value={filterApi.get("location")}
            onChange={filterApi.setter("location")}
          />

          <AnimatePresence>
            {(hasFilters || filterValues.mode !== "auto") && (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.1 }}
              >
                <Button
                  variant="inverted"
                  icon={<IconClose />}
                  iconAlign="right"
                  onClick={() => filterApi.setAll({ location: [], service: [], category: [], all: "" })}
                >
                  Reset
                </Button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div> */}

      {/* Main Display */}
      <div>
        <AnimatePresence mode="wait">
          <StaggerTransitionRoot key={hasFilters ? "filtered-" + filterKey : "featured"}>
            <motion.div
              variants={{
                hide: {},
                show: { transition: { delayChildren: stagger(0.1) } },
              }}
              initial="hide"
              animate="show"
              exit="hide"
            >
              {resultDisplay}
            </motion.div>
          </StaggerTransitionRoot>
        </AnimatePresence>
      </div>
    </div>
  )
})
